// Notification digest on the web — the PAGE half (KSE-0185 push, cross-device
// clearing). The service worker half is public/sw.js.
//
// The rule (shared with the native apps): one Swift digest decides what to
// show and what to clear on every StateChange, wherever it arrives, from what
// the platform ACTUALLY displays plus persisted cursors. On the web:
//
//   - a page is open  → the SW asks the page (MessageChannel), the page runs
//     the core's `notificationDigest` op (authoritative: it owns the OPFS
//     store) and replies {show, clear, badge}; the page also runs the digest
//     on its own — visibility, a forwarded StateChange, a local read/archive/
//     delete — and applies clears + the app badge itself (foreground shows
//     nothing);
//   - no page answers → the SW needs accounts + credentials + cursors to run
//     the memory-mode core itself. Those live in ONE encrypted IndexedDB
//     record (db `lately-notify`, store `records`, key `v1`: AES-GCM ciphertext
//     of {accounts:[{accountStorageKey, accountId, serverURL, username,
//     credential, session}], cursors, updatedAt}; store `keys`, key `v1`: the
//     non-extractable AES-GCM CryptoKey). This module keeps that record
//     current (debounced 2 s) while the user has "Notify me when Lately is
//     closed" on; the SW rewrites cursors only, and the page merges by
//     updatedAt. Off = record + key deleted. Until the shared-auth PRF flow
//     lands the key is generated at random on this device (still
//     non-extractable, but any script on this origin can use it — see
//     IMPLEMENTATION.md); the PRF-derived key (HKDF info "lately/notify/v1")
//     replaces it without a format change.
//   - the record exists only for a consumer: the SW reports whether this
//     build ships /lately-swift-core/headless.js (self.LatelyHeadlessCore
//     .notificationDigest over the wasm export lately_notification_digest).
//     Without it the toggle is not offered, enable() writes nothing, and a
//     record left by an earlier build is deleted — no credential at rest for
//     nothing.
//   - the page never presents: its own digests (visibility, unread changes,
//     a forwarded StateChange) run with mayShow = document visible, so a
//     hidden tab or PWA does not consume new-mail notifications the SW must
//     still show; when it answers the SW it passes the bare `changed`
//     (mayShow default true — the SW is the presenter).
//
// Nothing here is mail logic: the reconciler is the core's (mirrored types in
// shell-types.ts). The browser APIs sit behind NotifyPlatform / the record
// store so notify.test.ts drives the whole flow in-process.

import type { ReactNode } from "react";
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

import type { ShellClient } from "./shell";
import {
  type DeliveredNotification,
  type MailShellSnapshot,
  NOTIFICATION_TAG_PREFIX,
  type NotificationCursor,
  type NotificationDigest,
  type NotificationDigestTrigger,
  type NotificationRef,
  type ShellCommand,
} from "./shell-types";

// ---------------------------------------------------------------------------
// The record

export type NotifyCredential =
  | { kind: "password"; secret: string }
  | { kind: "bearer"; secret: string };

export interface NotifyRecordAccount {
  accountStorageKey: string;
  accountId: string;
  serverURL: string;
  username: string;
  credential: NotifyCredential;
  // JMAPClientSnapshot from the shell snapshot, so the SW skips discovery.
  session?: unknown;
}

export interface NotifyRecord {
  accounts: NotifyRecordAccount[];
  cursors: Record<string, NotificationCursor>;
  updatedAt: string;
}

// What the page knows about a signed-in account's credential (from the auth
// layer); joined with the shell snapshot to build the record.
export interface NotifyCredentialSource {
  serverURL: string;
  username: string;
  credential: NotifyCredential;
}

export interface NotifyRecordStore {
  read(): Promise<NotifyRecord | null>;
  write(record: NotifyRecord): Promise<void>;
  clear(): Promise<void>;
}

export const NOTIFY_DB = "lately-notify";
export const NOTIFY_RECORDS_STORE = "records";
export const NOTIFY_KEYS_STORE = "keys";
export const NOTIFY_RECORD_ID = "v1";

const enc = new TextEncoder();
const dec = new TextDecoder();

// Minimal IndexedDB access — same layout the service worker reads.
function requestDone<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function openNotifyDatabase(idb: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = idb.open(NOTIFY_DB, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(NOTIFY_RECORDS_STORE)) {
        db.createObjectStore(NOTIFY_RECORDS_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(NOTIFY_KEYS_STORE)) {
        db.createObjectStore(NOTIFY_KEYS_STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export interface IndexedDbNotifyRecordStoreOptions {
  indexedDB?: IDBFactory;
  subtle?: SubtleCrypto;
  random?: (bytes: Uint8Array) => Uint8Array;
}

export class IndexedDbNotifyRecordStore implements NotifyRecordStore {
  private readonly idb: IDBFactory;
  private readonly subtle: SubtleCrypto;
  private readonly random: (bytes: Uint8Array) => Uint8Array;

  constructor(options: IndexedDbNotifyRecordStoreOptions = {}) {
    this.idb = options.indexedDB ?? indexedDB;
    this.subtle = options.subtle ?? crypto.subtle;
    this.random = options.random ?? ((bytes) => crypto.getRandomValues(bytes));
  }

  private async withDb<T>(run: (db: IDBDatabase) => Promise<T>): Promise<T> {
    const db = await openNotifyDatabase(this.idb);
    try {
      return await run(db);
    } finally {
      db.close();
    }
  }

  private async key(create: boolean): Promise<CryptoKey | null> {
    return this.withDb(async (db) => {
      const read = db.transaction(NOTIFY_KEYS_STORE, "readonly");
      const existing = (await requestDone(
        read.objectStore(NOTIFY_KEYS_STORE).get(NOTIFY_RECORD_ID),
      )) as { id: string; key: CryptoKey } | undefined;
      if (existing?.key) {
        return existing.key;
      }
      if (!create) {
        return null;
      }
      const key = await this.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
        "encrypt",
        "decrypt",
      ]);
      const write = db.transaction(NOTIFY_KEYS_STORE, "readwrite");
      write.objectStore(NOTIFY_KEYS_STORE).put({ id: NOTIFY_RECORD_ID, key });
      await transactionDone(write);
      return key;
    });
  }

  async read(): Promise<NotifyRecord | null> {
    const key = await this.key(false);
    if (!key) {
      return null;
    }
    const stored = await this.withDb(async (db) => {
      const tx = db.transaction(NOTIFY_RECORDS_STORE, "readonly");
      return (await requestDone(tx.objectStore(NOTIFY_RECORDS_STORE).get(NOTIFY_RECORD_ID))) as
        | { id: string; iv: Uint8Array; ciphertext: ArrayBuffer }
        | undefined;
    });
    if (!stored) {
      return null;
    }
    try {
      const plaintext = await this.subtle.decrypt(
        { name: "AES-GCM", iv: stored.iv },
        key,
        stored.ciphertext,
      );
      const parsed = JSON.parse(dec.decode(plaintext)) as NotifyRecord;
      return normalizeRecord(parsed);
    } catch {
      return null;
    }
  }

  async write(record: NotifyRecord): Promise<void> {
    const key = await this.key(true);
    if (!key) {
      throw new Error("no notification record key");
    }
    const iv = this.random(new Uint8Array(12));
    const ciphertext = await this.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      enc.encode(JSON.stringify(record)),
    );
    await this.withDb(async (db) => {
      const tx = db.transaction(NOTIFY_RECORDS_STORE, "readwrite");
      tx.objectStore(NOTIFY_RECORDS_STORE).put({
        id: NOTIFY_RECORD_ID,
        iv,
        ciphertext,
        updatedAt: record.updatedAt,
      });
      await transactionDone(tx);
    });
  }

  async clear(): Promise<void> {
    await this.withDb(async (db) => {
      const tx = db.transaction([NOTIFY_RECORDS_STORE, NOTIFY_KEYS_STORE], "readwrite");
      tx.objectStore(NOTIFY_RECORDS_STORE).clear();
      tx.objectStore(NOTIFY_KEYS_STORE).clear();
      await transactionDone(tx);
    });
  }
}

export class MemoryNotifyRecordStore implements NotifyRecordStore {
  record: NotifyRecord | null = null;
  writes = 0;
  async read(): Promise<NotifyRecord | null> {
    return this.record ? structuredClone(this.record) : null;
  }
  async write(record: NotifyRecord): Promise<void> {
    this.writes += 1;
    this.record = structuredClone(record);
  }
  async clear(): Promise<void> {
    this.record = null;
  }
}

function normalizeRecord(value: unknown): NotifyRecord | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Partial<NotifyRecord>;
  return {
    accounts: Array.isArray(record.accounts) ? record.accounts : [],
    cursors: record.cursors && typeof record.cursors === "object" ? record.cursors : {},
    updatedAt: typeof record.updatedAt === "string" ? record.updatedAt : new Date(0).toISOString(),
  };
}

// Newest cursor per account wins (the SW and the page both write cursors).
export function mergeCursors(
  left: Record<string, NotificationCursor>,
  right: Record<string, NotificationCursor>,
): Record<string, NotificationCursor> {
  const merged: Record<string, NotificationCursor> = { ...left };
  for (const [key, cursor] of Object.entries(right)) {
    const current = merged[key];
    if (!current || Date.parse(cursor.updatedAt ?? "") >= Date.parse(current.updatedAt ?? "")) {
      merged[key] = cursor;
    }
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Platform adapter

// SW → page: {type:'lately-notification-digest', requestId, changed, delivered}
// with a MessageChannel port to answer on; the answer is
// {type:'lately-notification-digest-result', requestId, digest} or
// {…, unavailable: true, reason}.
export const NOTIFICATION_DIGEST_REQUEST = "lately-notification-digest";
export const NOTIFICATION_DIGEST_RESULT = "lately-notification-digest-result";
export const NOTIFICATION_OPEN = "lately-notification-open";

export interface DigestRequest {
  requestId: string;
  changed: Record<string, Record<string, string>> | null;
  delivered: DeliveredNotification[];
}

export type DigestReply =
  | { type: typeof NOTIFICATION_DIGEST_RESULT; requestId: string; digest: NotificationDigest }
  | {
      type: typeof NOTIFICATION_DIGEST_RESULT;
      requestId: string;
      unavailable: true;
      reason: string;
    };

export interface NotifyPlatform {
  readonly supported: boolean;
  // Every Lately notification the browser currently shows.
  getDelivered(): Promise<DeliveredNotification[]>;
  close(tags: string[]): Promise<void>;
  setBadge(count: number | null): Promise<void>;
  // SW → page digest requests; returns the unsubscribe function.
  listen(
    handler: (request: DigestRequest, reply: (reply: DigestReply) => void) => void,
  ): () => void;
  // Page visibility (a foreground digest runs when the page becomes visible).
  onVisible(handler: () => void): () => void;
  // The user clicked a thread notification (SW → page lately-notification-open).
  onOpen(handler: (lately: NotificationRef) => void): () => void;
  // Whether the page is visible right now (mayShow for the page's own digests).
  isVisible(): boolean;
  // Whether the controlling service worker has the headless core
  // (/lately-swift-core/headless.js -> self.LatelyHeadlessCore). Asked over
  // postMessage; false when no worker answers in time.
  headlessCoreAvailable(): Promise<boolean>;
}

export const NOTIFY_CAPABILITIES = "lately-notify-capabilities";
const CAPABILITIES_TIMEOUT_MS = 3_000;

interface LatelyNotificationLike {
  tag: string;
  data?: { lately?: Partial<DeliveredNotification> } | null;
  close(): void;
}

// The DeliveredNotification a Notification stands for, or null when it is
// not one of ours (no data.lately or a foreign tag).
export function deliveredFromNotification(
  notification: LatelyNotificationLike,
): DeliveredNotification | null {
  const lately = notification.data?.lately;
  if (
    !lately ||
    typeof notification.tag !== "string" ||
    !notification.tag.startsWith(NOTIFICATION_TAG_PREFIX) ||
    typeof lately.accountStorageKey !== "string" ||
    typeof lately.accountId !== "string" ||
    typeof lately.threadId !== "string" ||
    typeof lately.emailId !== "string"
  ) {
    return null;
  }
  return {
    identifier: notification.tag,
    accountStorageKey: lately.accountStorageKey,
    accountId: lately.accountId,
    threadId: lately.threadId,
    emailId: lately.emailId,
  };
}

export class BrowserNotifyPlatform implements NotifyPlatform {
  readonly supported: boolean;

  constructor() {
    this.supported =
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      typeof window !== "undefined" &&
      "Notification" in window;
  }

  private async registration(): Promise<ServiceWorkerRegistration | null> {
    if (!this.supported) {
      return null;
    }
    return (await navigator.serviceWorker.getRegistration("/")) ?? null;
  }

  async getDelivered(): Promise<DeliveredNotification[]> {
    const registration = await this.registration();
    if (!registration || typeof registration.getNotifications !== "function") {
      return [];
    }
    const notifications = await registration.getNotifications();
    return notifications
      .map((notification) => deliveredFromNotification(notification as LatelyNotificationLike))
      .filter((item): item is DeliveredNotification => item !== null);
  }

  async close(tags: string[]): Promise<void> {
    if (tags.length === 0) {
      return;
    }
    const registration = await this.registration();
    if (!registration || typeof registration.getNotifications !== "function") {
      return;
    }
    const wanted = new Set(tags);
    for (const notification of await registration.getNotifications()) {
      if (wanted.has(notification.tag)) {
        notification.close();
      }
    }
  }

  async setBadge(count: number | null): Promise<void> {
    const nav = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    try {
      if (count === null || count <= 0) {
        await nav.clearAppBadge?.();
      } else {
        await nav.setAppBadge?.(count);
      }
    } catch {
      // Badging is best effort (not every browser / install mode has it).
    }
  }

  listen(
    handler: (request: DigestRequest, reply: (reply: DigestReply) => void) => void,
  ): () => void {
    if (!this.supported) {
      return () => {};
    }
    const onMessage = (event: MessageEvent) => {
      const data = event.data as Partial<DigestRequest> & { type?: unknown };
      if (!data || data.type !== NOTIFICATION_DIGEST_REQUEST) {
        return;
      }
      const port = event.ports?.[0];
      const reply = (message: DigestReply) => {
        if (port) {
          port.postMessage(message);
        } else {
          navigator.serviceWorker.controller?.postMessage(message);
        }
      };
      handler(
        {
          requestId: String(data.requestId ?? ""),
          changed:
            data.changed && typeof data.changed === "object"
              ? (data.changed as Record<string, Record<string, string>>)
              : null,
          delivered: Array.isArray(data.delivered) ? data.delivered : [],
        },
        reply,
      );
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }

  onVisible(handler: () => void): () => void {
    if (typeof document === "undefined") {
      return () => {};
    }
    const onChange = () => {
      if (document.visibilityState === "visible") {
        handler();
      }
    };
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }

  onOpen(handler: (lately: NotificationRef) => void): () => void {
    if (!this.supported) {
      return () => {};
    }
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; lately?: Partial<NotificationRef> } | null;
      if (!data || data.type !== NOTIFICATION_OPEN || !data.lately) {
        return;
      }
      const lately = data.lately;
      if (
        typeof lately.accountStorageKey === "string" &&
        typeof lately.accountId === "string" &&
        typeof lately.threadId === "string" &&
        typeof lately.emailId === "string"
      ) {
        handler(lately as NotificationRef);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }

  isVisible(): boolean {
    return typeof document === "undefined" || document.visibilityState === "visible";
  }

  async headlessCoreAvailable(): Promise<boolean> {
    if (!this.supported) {
      return false;
    }
    const registration = await this.registration().catch(() => null);
    const worker =
      registration?.active ?? registration?.waiting ?? navigator.serviceWorker.controller ?? null;
    if (!worker || typeof MessageChannel === "undefined") {
      return false;
    }
    return new Promise<boolean>((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(() => {
        channel.port1.close();
        resolve(false);
      }, CAPABILITIES_TIMEOUT_MS);
      channel.port1.onmessage = (event: MessageEvent) => {
        clearTimeout(timer);
        channel.port1.close();
        const data = event.data as { type?: unknown; headlessCore?: unknown } | null;
        resolve(data?.type === NOTIFY_CAPABILITIES && data.headlessCore === true);
      };
      try {
        worker.postMessage({ type: NOTIFY_CAPABILITIES }, [channel.port2]);
      } catch {
        clearTimeout(timer);
        resolve(false);
      }
    });
  }
}

export class MockNotifyPlatform implements NotifyPlatform {
  readonly supported = true;
  delivered: DeliveredNotification[] = [];
  closed: string[] = [];
  badge: number | null | undefined = undefined;
  private handler: ((request: DigestRequest, reply: (reply: DigestReply) => void) => void) | null =
    null;
  private visible: (() => void) | null = null;
  private opener: ((lately: NotificationRef) => void) | null = null;

  async getDelivered(): Promise<DeliveredNotification[]> {
    return [...this.delivered];
  }
  async close(tags: string[]): Promise<void> {
    this.closed.push(...tags);
    this.delivered = this.delivered.filter((item) => !tags.includes(item.identifier));
  }
  async setBadge(count: number | null): Promise<void> {
    this.badge = count;
  }
  listen(
    handler: (request: DigestRequest, reply: (reply: DigestReply) => void) => void,
  ): () => void {
    this.handler = handler;
    return () => {
      if (this.handler === handler) {
        this.handler = null;
      }
    };
  }
  onVisible(handler: () => void): () => void {
    this.visible = handler;
    return () => {
      this.visible = null;
    };
  }
  onOpen(handler: (lately: NotificationRef) => void): () => void {
    this.opener = handler;
    return () => {
      this.opener = null;
    };
  }
  visibleNow = true;
  headlessCore = true;
  isVisible(): boolean {
    return this.visibleNow;
  }
  async headlessCoreAvailable(): Promise<boolean> {
    return this.headlessCore;
  }
  // Test hooks.
  open(lately: NotificationRef) {
    this.opener?.(lately);
  }
  request(request: DigestRequest): Promise<DigestReply> {
    return new Promise((resolve, reject) => {
      if (!this.handler) {
        reject(new Error("no digest listener"));
        return;
      }
      this.handler(request, resolve);
    });
  }
  becomeVisible() {
    this.visible?.();
  }
}

// ---------------------------------------------------------------------------
// Controller

export type NotifyState =
  | { status: "unsupported" }
  // This build's service worker has no headless core to consume the record:
  // the toggle is not offered and nothing is kept at rest.
  | { status: "unavailable" }
  // "Notify me when Lately is closed" is off: the page still answers the SW
  // and clears in the foreground; the SW without a page shows the generic line.
  | { status: "off" }
  | { status: "on"; accounts: number; lastWrittenAt: number | null }
  | { status: "error"; message: string };

const WANTED_KEY = "lately.web.notify.wanted";
const RECORD_DEBOUNCE_MS = 2_000;
const LOCAL_DIGEST_DEBOUNCE_MS = 1_500;

export interface NotifyTimers {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const realTimers: NotifyTimers = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

function readWanted(): boolean {
  try {
    return localStorage.getItem(WANTED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeWanted(value: boolean) {
  try {
    if (value) {
      localStorage.setItem(WANTED_KEY, "1");
    } else {
      localStorage.removeItem(WANTED_KEY);
    }
  } catch {
    // private mode
  }
}

export type NotifyShell = Pick<ShellClient, "engine" | "snapshot" | "onSnapshot" | "command">;

export interface NotifyControllerOptions {
  attach?: boolean;
  timers?: NotifyTimers;
  // Opt-in as remembered on this browser (default: localStorage).
  wanted?: { read(): boolean; write(value: boolean): void };
}

type Listener = () => void;

// The unread signature of a snapshot: which threads are unread. A change
// (local read/archive/delete, or a sync that brought remote changes) is what
// prompts an unprompted foreground digest.
function unreadSignature(snapshot: MailShellSnapshot): string {
  return snapshot.threadGroups
    .map((group) => `${group.threadId}:${group.hasUnread ? 1 : 0}`)
    .join(",");
}

function isUnknownOp(cause: unknown): boolean {
  const message = cause instanceof Error ? cause.message : String(cause);
  return /unknown (shell )?command|unknown op|unsupported|not (a )?known|notificationDigest/i.test(
    message,
  );
}

function normalizeServerURL(value: string): string {
  return value.trim().toLowerCase().replace(/\/+$/, "");
}

export class NotifyController {
  readonly platform: NotifyPlatform;
  private readonly shell: NotifyShell;
  private readonly store: NotifyRecordStore;
  private readonly credentials: () => NotifyCredentialSource[];
  private readonly timers: NotifyTimers;
  private readonly wantedStore: { read(): boolean; write(value: boolean): void };
  private readonly listeners = new Set<Listener>();
  private current: NotifyState;
  private snapshot: MailShellSnapshot;
  private cursors: Record<string, NotificationCursor> = {};
  private attached = false;
  private stopListening: (() => void) | null = null;
  private stopVisible: (() => void) | null = null;
  private stopOpen: (() => void) | null = null;
  private unsubscribeSnapshot: (() => void) | null = null;
  private recordTimer: unknown = null;
  private digestTimer: unknown = null;
  private lastSignature: string | null = null;
  private lastWritten: string | null = null;
  private lastWrittenAt: number | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  // Set when the core answered notificationDigest with "unknown op": an
  // older core; stop asking until the next attach.
  private coreLacksDigest = false;
  // null until the SW answered the capability probe; false = no headless
  // core in this build (record writer stays off).
  private headlessAvailable: boolean | null = null;

  constructor(
    shell: NotifyShell,
    platform: NotifyPlatform,
    store: NotifyRecordStore,
    credentials: () => NotifyCredentialSource[],
    options: NotifyControllerOptions = {},
  ) {
    this.shell = shell;
    this.platform = platform;
    this.store = store;
    this.credentials = credentials;
    this.timers = options.timers ?? realTimers;
    this.wantedStore = options.wanted ?? { read: readWanted, write: writeWanted };
    this.snapshot = shell.snapshot;
    this.current = platform.supported
      ? this.wantedStore.read()
        ? { status: "on", accounts: 0, lastWrittenAt: null }
        : { status: "off" }
      : { status: "unsupported" };
    if (options.attach !== false) {
      this.attach();
    }
  }

  get state(): NotifyState {
    return this.current;
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  attach() {
    if (this.attached) {
      return;
    }
    this.attached = true;
    this.coreLacksDigest = false;
    if (this.platform.supported) {
      this.stopListening = this.platform.listen((request, reply) => this.answer(request, reply));
      this.stopVisible = this.platform.onVisible(() => this.scheduleLocalDigest(0));
      this.stopOpen = this.platform.onOpen((lately) => this.openThread(lately));
    }
    this.unsubscribeSnapshot = this.shell.onSnapshot((snapshot) => this.applySnapshot(snapshot));
    this.applySnapshot(this.shell.snapshot);
    if (this.platform.supported) {
      void this.enqueue(() => this.probeHeadless());
    }
  }

  // Ask the SW once per attach whether it can consume the record. Without a
  // consumer the record is not written (and one left behind is deleted).
  private async probeHeadless(): Promise<boolean> {
    if (this.headlessAvailable !== null) {
      return this.headlessAvailable;
    }
    const available = await this.platform.headlessCoreAvailable().catch(() => false);
    if (!this.attached) {
      return available;
    }
    this.headlessAvailable = available;
    if (!available) {
      // Nothing can read it: drop what an earlier build may have kept. The
      // remembered wish stays for a build that has the core again.
      await this.store.clear().catch(() => undefined);
      this.lastWritten = null;
      this.lastWrittenAt = null;
      this.set({ status: "unavailable" });
    } else if (this.wantedStore.read()) {
      this.set({ status: "on", accounts: 0, lastWrittenAt: null });
      await this.writeRecord();
    }
    return available;
  }

  detach() {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    this.headlessAvailable = null;
    this.stopListening?.();
    this.stopListening = null;
    this.stopVisible?.();
    this.stopVisible = null;
    this.stopOpen?.();
    this.stopOpen = null;
    this.unsubscribeSnapshot?.();
    this.unsubscribeSnapshot = null;
    if (this.recordTimer !== null) {
      this.timers.clearTimeout(this.recordTimer);
      this.recordTimer = null;
    }
    if (this.digestTimer !== null) {
      this.timers.clearTimeout(this.digestTimer);
      this.digestTimer = null;
    }
  }

  dispose() {
    this.detach();
    this.listeners.clear();
  }

  // "Notify me when Lately is closed": keep the encrypted record current.
  enable(): Promise<void> {
    if (!this.platform.supported) {
      return Promise.resolve();
    }
    return this.enqueue(async () => {
      if (!(await this.probeHeadless())) {
        this.set({ status: "unavailable" });
        return;
      }
      this.wantedStore.write(true);
      this.set({ status: "on", accounts: 0, lastWrittenAt: null });
      await this.writeRecord();
    });
  }

  // Off: the record and its key are deleted; the SW falls back to the
  // generic notification when no page is open.
  disable(): Promise<void> {
    this.wantedStore.write(false);
    this.set(this.platform.supported ? { status: "off" } : { status: "unsupported" });
    return this.enqueue(async () => {
      await this.store.clear().catch(() => undefined);
      this.lastWritten = null;
      this.lastWrittenAt = null;
    });
  }

  // Sign-out of the last account: forget everything and clear what is shown.
  reset(): Promise<void> {
    this.wantedStore.write(false);
    this.set(this.platform.supported ? { status: "off" } : { status: "unsupported" });
    return this.enqueue(async () => {
      await this.store.clear().catch(() => undefined);
      this.lastWritten = null;
      this.lastWrittenAt = null;
      this.cursors = {};
      const delivered = await this.platform.getDelivered().catch(() => []);
      await this.platform.close(delivered.map((item) => item.identifier)).catch(() => undefined);
      await this.platform.setBadge(null).catch(() => undefined);
    });
  }

  // Run the digest now (unprompted or for a StateChange the page learned of)
  // and apply clears + badge; the page shows nothing. Resolves null when the
  // core cannot answer.
  digest(
    changed: Record<string, Record<string, string>> | null = null,
  ): Promise<NotificationDigest | null> {
    return this.enqueue(() =>
      this.runDigest({
        trigger: changed ? { kind: "stateChange", changed } : { kind: "foreground" },
      }),
    );
  }

  // ---- inputs -----------------------------------------------------------

  private applySnapshot(snapshot: MailShellSnapshot) {
    const previous = this.snapshot;
    this.snapshot = snapshot;
    const signature = unreadSignature(snapshot);
    const accountsChanged =
      JSON.stringify(previous.accounts.map((account) => [account.storageKey, account.session])) !==
      JSON.stringify(snapshot.accounts.map((account) => [account.storageKey, account.session]));
    if (accountsChanged && this.wantedStore.read()) {
      this.scheduleRecordWrite();
    }
    if (this.lastSignature !== null && signature !== this.lastSignature && !snapshot.isRefreshing) {
      this.scheduleLocalDigest(LOCAL_DIGEST_DEBOUNCE_MS);
    }
    if (!snapshot.isRefreshing) {
      this.lastSignature = signature;
    }
  }

  private answer(request: DigestRequest, reply: (reply: DigestReply) => void) {
    void this.enqueue(async () => {
      const digest = await this.runDigest({
        changed: request.changed,
        delivered: request.delivered,
        forWorker: true,
      });
      if (digest) {
        reply({ type: NOTIFICATION_DIGEST_RESULT, requestId: request.requestId, digest });
      } else {
        reply({
          type: NOTIFICATION_DIGEST_RESULT,
          requestId: request.requestId,
          unavailable: true,
          reason: this.coreLacksDigest ? "core has no notificationDigest" : "digest failed",
        });
      }
    });
  }

  // A clicked thread notification: select the thread when its account is the
  // open one (switching accounts is the auth layer's; the thread is a tap away
  // after the switch).
  private openThread(lately: NotificationRef) {
    const active =
      this.snapshot.activeAccountStorageKey ??
      this.snapshot.accounts.find((account) => account.isActive)?.storageKey ??
      null;
    if (active !== lately.accountStorageKey) {
      return;
    }
    void this.enqueue(async () => {
      await this.shell
        .command({ op: "selectThread", threadId: lately.threadId })
        .catch((cause) => console.warn("Lately notifications: selectThread failed", cause));
    });
  }

  // ---- the digest ------------------------------------------------------

  // Two callers: the SW (forWorker: bare `changed`, the SW presents, mayShow
  // stays at the core's default true) and the page itself (a trigger; mayShow
  // = visible, so a hidden page only clears + badges and leaves new mail in
  // the cursor for the SW).
  private async runDigest(input: {
    changed?: Record<string, Record<string, string>> | null;
    trigger?: NotificationDigestTrigger;
    delivered?: DeliveredNotification[];
    forWorker?: boolean;
  }): Promise<NotificationDigest | null> {
    if (!this.attached || this.coreLacksDigest) {
      return null;
    }
    if (this.snapshot.accounts.length === 0) {
      return null;
    }
    const delivered = input.delivered ?? (await this.platform.getDelivered().catch(() => []));
    const applyLocally = !input.forWorker;
    let digest: NotificationDigest;
    try {
      const command: Extract<ShellCommand, { op: "notificationDigest" }> = input.forWorker
        ? { op: "notificationDigest", delivered, changed: input.changed ?? null }
        : {
            op: "notificationDigest",
            delivered,
            trigger: input.trigger ?? { kind: "foreground" },
            mayShow: this.platform.isVisible(),
          };
      const result = await this.shell.command(command);
      digest = result.result;
    } catch (cause) {
      if (isUnknownOp(cause)) {
        this.coreLacksDigest = true;
      } else {
        console.warn("Lately notifications: digest failed", cause);
      }
      return null;
    }
    this.cursors = mergeCursors(this.cursors, digest.cursors ?? {});
    if (applyLocally) {
      // Foreground: clears and badge only. What the SW asked for it applies itself.
      await this.platform.close(digest.clear.map((item) => item.identifier)).catch(() => undefined);
      if (digest.badge !== undefined) {
        await this.platform.setBadge(digest.badge ?? null).catch(() => undefined);
      }
    }
    if (this.wantedStore.read()) {
      this.scheduleRecordWrite();
    }
    return digest;
  }

  private scheduleLocalDigest(ms: number) {
    if (this.digestTimer !== null) {
      this.timers.clearTimeout(this.digestTimer);
    }
    this.digestTimer = this.timers.setTimeout(() => {
      this.digestTimer = null;
      void this.enqueue(() => this.runDigest({ trigger: { kind: "foreground" } }));
    }, ms);
  }

  // ---- the record ------------------------------------------------------

  private scheduleRecordWrite() {
    if (this.recordTimer !== null) {
      this.timers.clearTimeout(this.recordTimer);
    }
    this.recordTimer = this.timers.setTimeout(() => {
      this.recordTimer = null;
      void this.enqueue(() => this.writeRecord());
    }, RECORD_DEBOUNCE_MS);
  }

  // The record the SW needs: every stored account whose credential the page
  // knows, with its session snapshot, plus the newest cursors from either
  // side. Written only when it changed.
  buildRecordAccounts(): NotifyRecordAccount[] {
    const sources = this.credentials();
    const accounts: NotifyRecordAccount[] = [];
    for (const account of this.snapshot.accounts) {
      const accountId = account.session?.mailAccountId;
      if (!accountId) {
        continue;
      }
      const source = sources.find(
        (candidate) =>
          normalizeServerURL(candidate.serverURL) === normalizeServerURL(account.serverURL) &&
          candidate.username.toLowerCase() === account.username.toLowerCase(),
      );
      if (!source) {
        continue;
      }
      accounts.push({
        accountStorageKey: account.storageKey,
        accountId,
        serverURL: account.serverURL,
        username: account.username,
        credential: source.credential,
        session: account.session ?? undefined,
      });
    }
    return accounts;
  }

  private async writeRecord(): Promise<void> {
    if (!this.attached || !this.wantedStore.read() || this.headlessAvailable !== true) {
      return;
    }
    const stored = await this.store.read().catch(() => null);
    const cursors = mergeCursors(stored?.cursors ?? {}, this.cursors);
    this.cursors = cursors;
    const accounts = this.buildRecordAccounts();
    const comparable = JSON.stringify({ accounts, cursors });
    if (comparable === this.lastWritten) {
      return;
    }
    if (accounts.length === 0 && !stored) {
      this.set({ status: "on", accounts: 0, lastWrittenAt: this.lastWrittenAt });
      return;
    }
    try {
      await this.store.write({
        accounts,
        cursors,
        updatedAt: new Date(this.timers.now()).toISOString(),
      });
      this.lastWritten = comparable;
      this.lastWrittenAt = this.timers.now();
      this.set({ status: "on", accounts: accounts.length, lastWrittenAt: this.lastWrittenAt });
    } catch (cause) {
      this.set({
        status: "error",
        message: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }

  // ---- plumbing ---------------------------------------------------------

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const next = this.queue.then(task, task);
    this.queue = next.catch(() => {});
    return next;
  }

  private set(state: NotifyState) {
    if (JSON.stringify(state) === JSON.stringify(this.current)) {
      return;
    }
    this.current = state;
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (cause) {
        console.error("Lately notifications: listener failed", cause);
      }
    }
  }
}

export function createNotifyController(
  shell: NotifyShell,
  credentials: () => NotifyCredentialSource[],
  options: NotifyControllerOptions = {},
): NotifyController {
  const mock = shell.engine === "mock-shell";
  const platform = mock ? new MockNotifyPlatform() : new BrowserNotifyPlatform();
  const store: NotifyRecordStore =
    mock || typeof indexedDB === "undefined" || typeof crypto === "undefined" || !crypto.subtle
      ? new MemoryNotifyRecordStore()
      : new IndexedDbNotifyRecordStore();
  return new NotifyController(shell, platform, store, credentials, options);
}

// ---------------------------------------------------------------------------
// React bindings

const NotifyContext = createContext<NotifyController | null>(null);

export function NotifyProvider({
  shell,
  credentials,
  signedOut,
  children,
}: {
  shell: ShellClient;
  credentials: () => NotifyCredentialSource[];
  signedOut: boolean;
  children?: ReactNode;
}) {
  // The accessor changes identity per render; the controller reads through a
  // ref so it always sees the current one without being rebuilt.
  const credentialsRef = useRef(credentials);
  credentialsRef.current = credentials;
  const controller = useMemo(
    () => createNotifyController(shell, () => credentialsRef.current(), { attach: false }),
    [shell],
  );
  useEffect(() => {
    controller.attach();
    return () => controller.detach();
  }, [controller]);
  useEffect(() => {
    if (signedOut) {
      void controller.reset();
    }
  }, [controller, signedOut]);
  return createElement(NotifyContext.Provider, { value: controller }, children);
}

export function useNotify(): { controller: NotifyController; state: NotifyState } {
  const controller = useContext(NotifyContext);
  if (!controller) {
    throw new Error("useNotify requires a NotifyProvider ancestor");
  }
  const state = useSyncExternalStore(
    (onChange) => controller.onChange(onChange),
    () => controller.state,
    () => controller.state,
  );
  return { controller, state };
}
